import { useState } from 'react';
import { toast } from 'sonner';
import { Shield, Lock, Eye, EyeOff, ArrowLeft, CheckCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function ForgotPasswordPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [adminCode, setAdminCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNew, setShowNew] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [resetToken, setResetToken] = useState('');

  const verifyAdminCode = async (e) => {
    e.preventDefault();
    if (!adminCode.trim()) return;
    setLoading(true);
    try {
      const res = await fetch(API + '/auth/verify-reset-code', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code: adminCode })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Code invalide');
      setResetToken(data.reset_token);
      setStep(2);
      toast.success('Code validé — définissez votre nouveau mot de passe');
    } catch (err) {
      toast.error(err.message);
    } finally { setLoading(false); }
  };

  const resetPassword = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) { toast.error('Les mots de passe ne correspondent pas'); return; }
    if (newPassword.length < 8) { toast.error('Minimum 8 caractères'); return; }
    setLoading(true);
    try {
      const res = await fetch(API + '/auth/reset-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reset_token: resetToken, new_password: newPassword })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Erreur');
      setSuccess(true);
      toast.success('Mot de passe réinitialisé avec succès');
      setTimeout(() => navigate('/login'), 2000);
    } catch (err) {
      toast.error(err.message);
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center p-6">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <h1 className="font-heading text-3xl font-bold text-[#EDEDED] uppercase tracking-widest mb-2">
            <span className="text-[#DC143C]">■</span> SELENA MATRIX <span className="text-[#DC143C]">■</span>
          </h1>
          <p className="font-mono text-xs text-[#888888] tracking-widest">RESET ACCESS CODE</p>
        </div>
        <div className="grid-card p-8 border-l-2 border-l-[#DC143C]">
          <button onClick={() => navigate('/login')} className="flex items-center gap-2 font-mono text-xs text-[#888888] hover:text-white mb-6 transition-colors">
            <ArrowLeft className="w-3 h-3" /> RETOUR AU LOGIN
          </button>
          {success ? (
            <div className="text-center space-y-4">
              <CheckCircle className="w-16 h-16 text-[#00FF9D] mx-auto" />
              <p className="font-heading text-lg text-[#00FF9D] uppercase">MOT DE PASSE RÉINITIALISÉ</p>
              <p className="font-mono text-xs text-[#888888]">Redirection vers le login...</p>
            </div>
          ) : step === 1 ? (
            <form onSubmit={verifyAdminCode} className="space-y-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="p-2 bg-[#DC143C]/10 border border-[#DC143C]/50">
                  <Shield className="w-5 h-5 text-[#DC143C]" />
                </div>
                <div>
                  <h2 className="font-heading text-sm font-bold text-[#EDEDED] uppercase">ÉTAPE 1 — CODE ADMIN</h2>
                  <p className="font-mono text-[10px] text-[#888888]">Entrez le code secret administrateur</p>
                </div>
              </div>
              <div>
                <label className="font-mono text-[10px] text-[#888888] uppercase tracking-widest block mb-2">CODE SECRET</label>
                <input type="password" value={adminCode} onChange={e => setAdminCode(e.target.value)} required
                  className="w-full bg-[#0A0A0A] border border-white/10 p-3 font-mono text-xs text-white focus:outline-none focus:border-[#DC143C]/50"
                  placeholder="••••••••••••••••" />
              </div>
              <button type="submit" disabled={loading || !adminCode}
                className="w-full py-3 bg-[#DC143C] text-white font-heading text-xs uppercase tracking-widest hover:bg-[#FF2E55] transition-colors disabled:opacity-30">
                {loading ? 'VÉRIFICATION...' : 'VALIDER LE CODE'}
              </button>
            </form>
          ) : (
            <form onSubmit={resetPassword} className="space-y-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="p-2 bg-[#00FF9D]/10 border border-[#00FF9D]/50">
                  <Lock className="w-5 h-5 text-[#00FF9D]" />
                </div>
                <div>
                  <h2 className="font-heading text-sm font-bold text-[#EDEDED] uppercase">ÉTAPE 2 — NOUVEAU MOT DE PASSE</h2>
                  <p className="font-mono text-[10px] text-[#888888]">Code validé — définissez votre accès</p>
                </div>
              </div>
              <div>
                <label className="font-mono text-[10px] text-[#888888] uppercase tracking-widest block mb-2">NOUVEAU MOT DE PASSE</label>
                <div className="relative">
                  <input type={showNew ? 'text' : 'password'} value={newPassword} onChange={e => setNewPassword(e.target.value)} required minLength={8}
                    className="w-full bg-[#0A0A0A] border border-white/10 p-3 pr-10 font-mono text-xs text-white focus:outline-none focus:border-[#00FF9D]/50"
                    placeholder="••••••••" />
                  <button type="button" onClick={() => setShowNew(!showNew)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#888888]">
                    {showNew ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              <div>
                <label className="font-mono text-[10px] text-[#888888] uppercase tracking-widest block mb-2">CONFIRMER</label>
                <input type="password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} required
                  className="w-full bg-[#0A0A0A] border border-white/10 p-3 font-mono text-xs text-white focus:outline-none focus:border-[#00FF9D]/50"
                  placeholder="••••••••" />
                {confirmPassword && newPassword !== confirmPassword && (
                  <p className="font-mono text-[10px] text-[#DC143C] mt-1">Ne correspondent pas</p>
                )}
              </div>
              <button type="submit" disabled={loading || !newPassword || newPassword !== confirmPassword}
                className="w-full py-3 bg-[#00FF9D] text-black font-heading text-xs uppercase tracking-widest hover:bg-[#00CC7A] transition-colors disabled:opacity-30">
                {loading ? 'RÉINITIALISATION...' : 'CONFIRMER LE NOUVEAU MOT DE PASSE'}
              </button>
            </form>
          )}
        </div>
        <p className="font-mono text-[10px] text-[#444444] text-center mt-6">UNAUTHORIZED ACCESS WILL BE LOGGED AND TRACED</p>
      </div>
    </div>
  );
}